import { LookbookSettingsGeneralFormSkeleton } from '@app/components/lookbook/LookbookSettingsPage/components';
import { LookbookSettingsGeneralPageForm } from '@app/components/lookbook/LookbookSettingsPage/LookbookSettingsGeneralPage';
import { Button } from '@app/components/various/Button';
import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, FormFieldset, FormGrid, InputField, ToggleField } from '@app/components/various/Form';
import { paths } from '@app/paths';
import { useDeleteLookbook } from '@app/services/hooks/lookbook/useDeleteLookbook';
import { useLookbookOwners } from '@app/services/hooks/lookbook/useLookbookOwners';
import { useConfirmationGuard } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

interface Props {
  isLoading: boolean;
}

export const LookbookSettingsGeneralFormFields = ({ isLoading }: Props) => {
  const { lookbookId } = useParams<{ lookbookId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'lookbook', 'confirmationConfig']);
  const confirmationGuard = useConfirmationGuard();
  const formMethods = useFormContext<LookbookSettingsGeneralPageForm>();

  const { mutate: deleteLookbook } = useDeleteLookbook();
  const { handleLoadMore, handleSearchChange, isFetching: isFetchingLookbookOwners, owners } = useLookbookOwners();

  const handleDeleteLookbook = confirmationGuard(() => ({
    onOk: () =>
      isDefined(lookbookId) &&
      deleteLookbook(lookbookId, {
        onSuccess: () => navigate(paths.LOOKBOOK),
      }),
    ...t('confirmationConfig:delete_lookbook', { returnObjects: true }),
  }));

  if (isLoading) {
    return <LookbookSettingsGeneralFormSkeleton />;
  }

  return (
    <div className="space-y-5">
      <section className="box space-y-5">
        <h3 className="heading-3">{t('common:general')}</h3>
        <p className="text-neutral-40 body-2">{t('lookbook:lookbook_settings_general_subtitle')}</p>
        <FormFieldset>
          <FormGrid rowGap="medium">
            <InputField label={t('common:name')} {...formMethods.register('name')} />
            <InputField label={t('common:market')} {...formMethods.register('marketName')} isReadonly />
            <AutocompleteField
              label={t('lookbook:lookbook_owner')}
              isLoadingOptions={isFetchingLookbookOwners}
              name="ownerId"
              onSearchChange={handleSearchChange}
              onLoadMore={handleLoadMore}
              placeholder={t('common:name_and_surname')}
              helpMessage={t('lookbook:lookbook_owner_help_message')}
              isRequired
            >
              {owners.map(({ id, name: ownerName }) => (
                <Option key={id} value={id}>
                  {ownerName}
                </Option>
              ))}
            </AutocompleteField>
            <ToggleField
              label={t('lookbook:allow_account_requesting')}
              fieldLabelClassName="justify-start"
              orientation="horizontal"
              {...formMethods.register('allowAccountRequest')}
            />
          </FormGrid>
        </FormFieldset>
      </section>

      <section className="box space-y-5">
        <h3 className="heading-3">{t('lookbook:remove_lookbook')}</h3>
        <p className="text-neutral-40 body-2">{t('lookbook:remove_lookbook_description')}</p>
        <Button color="danger" variant="text" size="large" onClick={handleDeleteLookbook}>
          {t('lookbook:remove_lookbook')}
        </Button>
      </section>
    </div>
  );
};
